import { Icon } from "@iconify/react";
import React, { useState } from "react";
import { Form, Spinner } from "react-bootstrap";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/bootstrap.css";
import { useFormik } from "formik";
import * as Yup from "yup";
import Select, { components } from "react-select";
import { useGetSupplierDestinationQuery } from "../../../../rtk-query/slices/select-options/selectOptionsSlice";
import { formatJobTitles } from "../../../../helpers/selectOptionFormatter";
import { usePostSupplierSavePrivateInformationMutation } from "../../../../rtk-query/slices/onboarding/personalDetailSlice";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

function PersonalsTabContent({ formState, updateFormState, setCurrentTab }) {
  const { data: jobTitleOptionsData } = useGetSupplierDestinationQuery();
  const navigate = useNavigate();
  const jobTitleOptions = jobTitleOptionsData?.data
    ? formatJobTitles(jobTitleOptionsData?.data)
    : [];

  const [
    postSupplierSavePrivateInformation,
    { isLoading: isPostSupplierSavePrivateInformationLoading },
  ] = usePostSupplierSavePrivateInformationMutation();

  const formik = useFormik({
    initialValues: {
      fullName: formState.fullName || "",
      jobTitle: formState.jobTitle || "",
      phoneNumber: formState.phoneNumber || "",
      termsAccepted: formState.termsAccepted || false,
      companyCertificate: null,
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      fullName: Yup.string().required("Please enter your full name"),
      jobTitle: Yup.object().required("Please enter your job title"),
      phoneNumber: Yup.string().required("Please enter your phone number"),
      companyCertificate: Yup.mixed()
        .required("Company certificate is required")
        .test(
          "fileSize",
          "File size is too large",
          (value) => value === null || (value && value.size <= 20000000) // 20MB
        )
        .test(
          "fileType",
          "Unsupported file format. Please upload a PDF, JPG, or PNG file.",
          (value) =>
            value === null ||
            (value &&
              ["application/pdf", "image/jpeg", "image/png"].includes(
                value.type
              ))
        ),
      termsAccepted: Yup.boolean().oneOf([true], "You must accept the terms"),
    }),

    onSubmit: async (values) => {
      updateFormState({
        fullName: values.fullName,
        jobTitle: values.jobTitle,
        phoneNumber: values.phoneNumber,
        termsAccepted: values.termsAccepted,
        companyCertificate: values.companyCertificate,
      });
      try {
        const formData = new FormData();
        formData.append("full_name", values.fullName);
        formData.append("job_title", values.jobTitle.value);
        formData.append("user_phone_number", values.phoneNumber);
        values.companyCertificate &&
          formData.append(
            "company_registration_certificate",
            values.companyCertificate
          );
        formData.append(
          "is_online_reservation",
          formState.bookingOption === "yes" ? 1 : 0
        );

        const response = await postSupplierSavePrivateInformation(formData);
        if (response?.data?.status) {
          toast.success(response?.data?.message, {
            position: "top-center",
            autoClose: 3000,
          });
          // navigate("/");
          setCurrentTab(5);
        }
        if (response?.error) {
          const errors = response?.error?.data?.errors;
          if (response?.error?.status == 401) {
            localStorage.removeItem("DODLoginToken");
            toast.error(response?.error?.data?.message, {
              position: "top-center",
              autoClose: 3000,
            });
            // navigate("/login");
            setCurrentTab(5);
          } else if (errors) {
            for (const field in errors) {
              if (errors.hasOwnProperty(field)) {
                const combinedErrors = errors[field].join(" ");
                // if (field === "supplier_type") {
                //   setFieldError("option", combinedErrors);
                // }
              }
            }
            toast.error(response?.error?.data?.message, {
              position: "top-center",
              autoClose: 3000,
            });
          } else {
            toast.error(response?.error?.data?.message, {
              position: "top-center",
              autoClose: 3000,
            });
          }
        }
      } catch (error) {
        console.log(error);
        toast.error("An unexpected error occurred. Please try again.");
      } finally {
      }
    },
  });
  const handleCertificateChange = (e) => {
    const file = e.target.files[0];
    formik.setFieldValue("companyCertificate", file); // Set file input value in Formik state
  };
  return (
    <div className="ob-pi-form-container">
      <h3 className="ob-pi-form-heading m-0">Personal details</h3>
      <p className="ob-pi-form-description m-0">
        These details are limited to your personal information only.
      </p>
      <Form className="mt-4" onSubmit={formik.handleSubmit}>
        <Form.Group controlId="experienceLocation">
          <p className="ob-pi-label m-0">
            Full name<span className="text-danger ps-1">*</span>
          </p>
          <p className="ob-pi-note pb-1 m-0">
            Full name for our system to contact you in case of any query or
            offerings
          </p>
          <div className="position-relative">
            <Form.Control
              type="text"
              name="fullName"
              placeholder="Full name"
              className="ob-pi-input mt-1"
              value={formik.values.fullName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {formik.errors.fullName && formik.touched.fullName && (
              <div className="invalid-feedback d-flex">
                {formik.errors.fullName}
              </div>
            )}
          </div>
        </Form.Group>

        <Form.Group controlId="aboutYourself" className="mt-4">
          <p className="ob-pi-label">
            Job title<span className="text-danger ps-1">*</span>
          </p>
          <p className="ob-pi-note pb-1 m-0">
            Your role within organization or if you are owner of the company, do
            mention that
          </p>
          <Select
            name="jobTitle"
            options={jobTitleOptions}
            placeholder="Search job title"
            className="ob-pi-input mt-1 p-0 form-control"
            value={formik.values.jobTitle}
            onChange={(e) => {
              console.log("====================================");
              console.log(e);
              console.log("====================================");
              formik.setFieldValue("jobTitle", e);
            }}
            components={{
              Control: ({ children, ...props }) => {
                return (
                  <components.Control {...props}>
                    {children}
                    <div className="select-icon">
                      <Icon icon="ic:baseline-keyboard-arrow-down" />
                    </div>
                  </components.Control>
                );
              },
            }}
          />

          {formik.errors.jobTitle && formik.touched.jobTitle && (
            <div className="invalid-feedback d-flex">
              {formik.errors.jobTitle}
            </div>
          )}
        </Form.Group>

        <Form.Group controlId="phoneNumber" className="mt-4">
          <p className="ob-pi-label">
            Preferred phone number<span className="text-danger ps-1">*</span>
          </p>
          <p className="ob-pi-note pb-1 m-0">
            Primary contact no that shall not be disclosed to anyone
          </p>
          <PhoneInput
            country={"ae"}
            placeholder="Company-contact-number-number"
            inputClass="ob-pi-input w-100 mt-1"
            value={formik.values.phoneNumber}
            onChange={(phone) => formik.setFieldValue("phoneNumber", phone)}
          />
          {formik.errors.phoneNumber && formik.touched.phoneNumber && (
            <div className="invalid-feedback d-flex">
              {formik.errors.phoneNumber}
            </div>
          )}
        </Form.Group>

        <Form.Group controlId="companyRegistrationNumber" className="mt-4">
          <p className="ob-pi-label">
            Company registration certificate
            <span className="text-danger ps-1">*</span>
          </p>
          <p className="ob-pi-note pb-1 m-0">
            Please provide your company registration certification here as a
            record
          </p>
          <div className="position-relative">
            <input
              type="file"
              id="certificateUpload"
              style={{
                height: "37px",
              }}
              accept="image/jpeg,image/png,application/pdf"
              onChange={handleCertificateChange}
              className="form-control mt-1"
            />
            {formik.errors.companyCertificate &&
              formik.touched.companyCertificate && (
                <div className="invalid-feedback d-flex">
                  {formik.errors.companyCertificate}
                </div>
              )}
          </div>
        </Form.Group>

        {/* Checkbox with text */}
        <Form.Group controlId="termsCheckbox" className="mt-4">
          <Form.Check
            type="checkbox"
            label="I have read and agree to comply with the terms of Leisure Time Out Supplier Agreement. "
            name="termsAccepted"
            checked={formik.values.termsAccepted}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            className="ob-pi-checkbox"
          />
          {formik.errors.termsAccepted && formik.touched.termsAccepted && (
            <div className="invalid-feedback d-flex">
              {formik.errors.termsAccepted}
            </div>
          )}
        </Form.Group>

        <div className="d-flex justify-content-between mt-5">
          <button type="button" className="back-btn">
            Back
          </button>
          <button
            disabled={isPostSupplierSavePrivateInformationLoading}
            type="submit"
            className="submit-btn"
          >
            Save & Continue{" "}
            {isPostSupplierSavePrivateInformationLoading && (
              <Spinner size="sm" />
            )}
          </button>
        </div>
      </Form>
    </div>
  );
}

export default PersonalsTabContent;
